import type { FragmentType } from 'src/gql';
import { useCallback, useRef, useState } from 'react';
import IconButton from '@savvy-kit/components/icon-button';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import smoothscroll from 'smoothscroll-polyfill';
import {
  BASE_CLIENT_ROADMAP_STAGE_ORDER,
  CLIENT_STAGE_TO_ROADMAP_STAGE,
  RoadmapStage,
} from 'src/constants/client-roadmap';
import { graphql, unmaskFragment } from 'src/gql';
import { useIsVerySmallScreen } from 'src/hooks/use-is-very-small-screen';
import { lastCompletedRoadMapStageIndex } from 'src/utils/client-roadmap';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import scrollRefIntoView from '@savvy/deprecated-shared/utils/scroll-ref-into-view';

import GradientProgressBar from '../loading-and-splash/gradient-progress';
import RoadmapStageCard from './roadmap-stage-card';

const HEADER_MAX_WIDTH = 390;
export const DEFAULT_ROADMAP_STAGE_CARD_WIDTH = 346;

export const ClientRoadmapFragment = graphql(/* GraphQL */ `
  fragment ClientRoadmap on Task {
    type
    ...RoadmapStageCard
  }
`);

function scrollToRoadmapStage(scrollContainer: HTMLDivElement, roadmapStage: RoadmapStage) {
  const roadmapCard = document.getElementsByClassName(`${roadmapStage}-card`)[0];
  smoothscroll.polyfill();

  if (isNil(roadmapCard)) {
    return;
  }
  scrollContainer.scrollTo({
    left:
      // @ts-expect-error - FIXME: This is using the wrong type
      Number(roadmapCard.offsetLeft) - Number(window.innerWidth) / 2 + DEFAULT_ROADMAP_STAGE_CARD_WIDTH,
    behavior: 'smooth',
  });
}

export type Props = {
  readonly taskFrags: FragmentType<typeof ClientRoadmapFragment>[];
};
export default function ClientRoadmap({ taskFrags }: Props) {
  const isSmallScreen = useIsSmallScreen();
  const isVerySmallScreen = useIsVerySmallScreen();
  const { user } = useAuth();
  const tasks = taskFrags.map((taskFrag) => unmaskFragment(ClientRoadmapFragment, taskFrag));

  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  setTimeout(() => {
    if (isSmallScreen) {
      scrollRefIntoView(scrollRef, {
        block: isVerySmallScreen ? 'end' : 'center',
      });
    }
  }, 30);

  const hasIntakeTask = tasks.filter((task) => task.type === 'initial_intake_form').length > 0;
  const hasAccountSetupTask = tasks.filter((task) => task.type === 'account_setup').length > 0;
  const orderedRoadmapStages = BASE_CLIENT_ROADMAP_STAGE_ORDER.filter((stage) => {
    if (stage === RoadmapStage.INTAKE_FLOW && !hasIntakeTask) {
      return false;
    } else if (stage === RoadmapStage.ACCOUNT_SETUP && !hasAccountSetupTask) {
      return false;
    }
    return true;
  });

  const clientRoadmapScrollRef = useCallback((node?: HTMLDivElement | null) => {
    if (isNil(node) || isSmallScreen) {
      return;
    }
    setScrollContainer(node);

    setTimeout(() => {
      const currentStageIndex = lastCompletedRoadMapStageIndex(orderedRoadmapStages, user);
      if (currentStageIndex) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
        scrollToRoadmapStage(node, orderedRoadmapStages[currentStageIndex]!);
      }
    });

    // Vertical scrolling results in horizontal scroll
    node.addEventListener('wheel', (evt: WheelEvent) => {
      evt.preventDefault();
      if (evt.deltaX !== 0) {
        // eslint-disable-next-line react-compiler/react-compiler -- FIXME: React considers this immutable. We should probably respect that
        node.scrollLeft += evt.deltaX;
      } else if (evt.deltaY !== 0) {
        node.scrollLeft += evt.deltaY;
      }
    });
  }, []);

  const roadmapStage = !isNil(user?.clientStage)
    ? CLIENT_STAGE_TO_ROADMAP_STAGE[user.clientStage]
    : RoadmapStage.SIT_BACK_AND_WAIT;

  const [focusedRoadmapStageIndex, setFocusedRoadmapStageIndex] = useState(orderedRoadmapStages.indexOf(roadmapStage));

  const prevButtonDisabled = focusedRoadmapStageIndex === 0;
  const nextButtonDisabled = focusedRoadmapStageIndex === orderedRoadmapStages.length - 1;
  const lastCompletedRoadmapStage = lastCompletedRoadMapStageIndex(orderedRoadmapStages, user);
  const regularScreenNavigationArrows = isSmallScreen ? null : (
    <div className="self-end">
      <div className="inline px-3">
        <IconButton
          variant="ghost"
          onClick={() => {
            setFocusedRoadmapStageIndex(focusedRoadmapStageIndex - 1);
            if (scrollContainer) {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
              scrollToRoadmapStage(scrollContainer, orderedRoadmapStages[focusedRoadmapStageIndex - 1]!);
            }
          }}
          disabled={prevButtonDisabled}
        >
          <ArrowLeft />
        </IconButton>
      </div>
      <IconButton
        variant="ghost"
        onClick={() => {
          setFocusedRoadmapStageIndex(focusedRoadmapStageIndex + 1);
          if (scrollContainer) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
            scrollToRoadmapStage(scrollContainer, orderedRoadmapStages[focusedRoadmapStageIndex + 1]!);
          }
        }}
        disabled={nextButtonDisabled}
      >
        <ArrowRight />
      </IconButton>
    </div>
  );

  return (
    <>
      <div className={cn('h-full bg-background-light', isSmallScreen && 'pb-24')}>
        {/* 100% opaque white background */}
        <div className="sticky top-0 z-10">
          <div
            className={cn(
              'flex flex-col justify-between bg-card',
              isSmallScreen ? 'px-6 pb-6 pt-16' : 'px-10 pb-10 pt-6',
            )}
          >
            <div className="flex flex-col gap-4" style={{ maxWidth: HEADER_MAX_WIDTH }}>
              {isSmallScreen && user?.firstName && <Text variant="h4">Hello, {user.firstName}!</Text>}
              <Text variant="h4">Welcome to your financial journey with Savvy</Text>
            </div>
            {regularScreenNavigationArrows}
          </div>

          <div className="mx-0 mb-8 w-full bg-card p-0">
            <GradientProgressBar
              value={(lastCompletedRoadMapStageIndex(orderedRoadmapStages, user) / orderedRoadmapStages.length) * 100}
              style={{
                height: 8,
                width: '100%',
              }}
            />
          </div>
        </div>
        <div
          ref={clientRoadmapScrollRef}
          className={cn(
            'flex w-auto client-roadmap-scroll-container',
            isSmallScreen ? 'flex-col gap-6' : 'relative flex-nowrap gap-8 overflow-x-scroll pl-8',
          )}
          style={isSmallScreen ? undefined : { paddingRight: `calc(50vw - ${DEFAULT_ROADMAP_STAGE_CARD_WIDTH / 2}px)` }}
        >
          {orderedRoadmapStages.map((roadmapStage, index) => (
            <div
              ref={index === lastCompletedRoadmapStage ? scrollRef : null}
              key={roadmapStage}
              className={cn(`${roadmapStage}-card`, 'flex-[0_0_auto] self-start', isSmallScreen && 'px-4')}
            >
              <RoadmapStageCard
                disabled={index < lastCompletedRoadmapStage}
                isCurrentStage={index === lastCompletedRoadmapStage}
                taskFrags={tasks}
                roadmapStage={roadmapStage}
                numStages={orderedRoadmapStages.length}
                stageNumber={index}
                width={isSmallScreen ? undefined : DEFAULT_ROADMAP_STAGE_CARD_WIDTH}
              />
            </div>
          ))}
        </div>
      </div>
      <style>
        {/* Hide scrollbar */}
        {`
          .client-roadmap-scroll-container::-webkit-scrollbar {
            display: none;
          }
      `}
      </style>
    </>
  );
}
